// Phase SIKA-4b — Bank-toward-goal modal.
//
// Source: SIKA_Independence_Architecture_v1.0 §10 Table 23 (Product
// Linking matrix) + §12 Table 24 row 21 (Cross-product win banking).
//
// User-initiated only. Never automatic. Composes I0 primitives only
// (Eyebrow, Pill, HairlineRow). Wires useBankExternalWin + useSikaGoals.

import { useMemo, useState } from "react";
import { Eyebrow, HairlineRow, Pill } from "../super-acca";
import { useSikaGoals } from "../../sika/useSikaGoals.ts";
import {
  useBankExternalWin,
  type SikaBankingSource,
} from "../../sika/useBankExternalWin.ts";

export interface BankWinModalProps {
  /** Source product the win came from. */
  source: SikaBankingSource;
  /** UUID of the won slip / chain / ticket. */
  sourceId: string;
  /** Amount available to bank (in GHS). */
  amount: number;
  /** Optional one-line label about the win, used in the modal copy. */
  sourceLabel?: string;
  /** Close handler — called on cancel + on successful banking. */
  onClose: () => void;
}

export function BankWinModal({
  source,
  sourceId,
  amount,
  sourceLabel,
  onClose,
}: BankWinModalProps): JSX.Element {
  const { goals, loading: goalsLoading } = useSikaGoals();
  const { bankWin, loading, error } = useBankExternalWin();
  const activeGoals = useMemo(
    () => goals.filter((g) => g.status === "active" || g.status === "in_progress"),
    [goals],
  );
  const [goalId, setGoalId] = useState<string>("");
  const [result, setResult] = useState<string | null>(null);

  async function handleConfirm(): Promise<void> {
    if (!goalId) {
      setResult("Pick a goal first.");
      return;
    }
    const res = await bankWin({
      goal_id: goalId,
      source,
      source_id: sourceId,
      amount,
    });
    if (!res.ok) {
      setResult(error ?? "Banking failed — try again.");
      return;
    }
    if (res.already_banked) {
      setResult("This win is already banked toward a goal.");
      return;
    }
    setResult(
      `Banked GH₵${amount.toFixed(2)} · new total GH₵${(res.new_current_amount ?? 0).toFixed(2)}.`,
    );
    setTimeout(onClose, 1500);
  }

  return (
    <div
      role="dialog"
      aria-label="Bank toward goal"
      data-testid="bank-win-modal"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        zIndex: 50,
      }}
    >
      <div
        style={{
          background: "var(--sa-bg-base, #0c0c0e)",
          border: "1px solid var(--sa-hairline)",
          padding: 22,
          width: "100%",
          maxWidth: 420,
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        <Eyebrow>BANK TOWARD GOAL</Eyebrow>

        <div
          style={{
            fontFamily: "var(--sa-font-mono)",
            fontSize: 12,
            color: "var(--sa-fg-muted)",
            lineHeight: 1.5,
          }}
        >
          Move GH₵{amount.toFixed(2)} from your {sourceLabel ?? source} win
          toward an active goal. This is optional — your win stays where it is
          if you skip.
        </div>

        {goalsLoading ? (
          <div
            style={{
              fontFamily: "var(--sa-font-mono)",
              fontSize: 12,
              color: "var(--sa-fg-muted)",
            }}
          >
            Loading goals…
          </div>
        ) : activeGoals.length === 0 ? (
          <Pill borderColor="var(--sa-hairline)">NO ACTIVE GOALS</Pill>
        ) : (
          <select
            data-testid="bank-win-goal-select"
            value={goalId}
            onChange={(e) => setGoalId(e.target.value)}
            style={{
              background: "transparent",
              border: "1px solid var(--sa-hairline)",
              color: "var(--sa-fg-primary)",
              padding: "8px 10px",
              fontFamily: "var(--sa-font-mono)",
              fontSize: 13,
            }}
          >
            <option value="">— Pick a goal —</option>
            {activeGoals.map((g) => (
              <option key={g.id} value={g.id}>
                {g.title} · GH₵{Number(g.current_amount ?? 0).toFixed(0)} /{" "}
                {Number(g.target_amount ?? 0).toFixed(0)}
              </option>
            ))}
          </select>
        )}

        {result ? (
          <HairlineRow>
            <div
              style={{
                fontFamily: "var(--sa-font-mono)",
                fontSize: 12,
                color: "var(--sa-fg-muted)",
              }}
            >
              {result}
            </div>
          </HairlineRow>
        ) : null}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={onClose}
            data-testid="bank-win-cancel"
            style={{
              background: "transparent",
              border: "1px solid var(--sa-hairline)",
              color: "var(--sa-fg-muted)",
              padding: "6px 14px",
              fontFamily: "var(--sa-font-mono)",
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            CANCEL
          </button>
          <button
            type="button"
            disabled={loading || !goalId || activeGoals.length === 0}
            onClick={handleConfirm}
            data-testid="bank-win-confirm"
            style={{
              background: "transparent",
              border: "1px solid var(--sa-accent-saffron)",
              color: "var(--sa-accent-saffron)",
              padding: "6px 14px",
              fontFamily: "var(--sa-font-mono)",
              fontSize: 12,
              cursor: "pointer",
              opacity: loading ? 0.5 : 1,
            }}
          >
            {loading ? "BANKING…" : "BANK"}
          </button>
        </div>
      </div>
    </div>
  );
}
